import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Check, MessageSquareQuote, RotateCcw, Star } from "lucide-react";
import { getFunnel, getFunnelSteps, getFeedback, setFeedbackStatus } from "../../business/GrowthBusiness";
import { Avatar, Badge, Button, Card, CardTitle, EmptyState, Page, PageHeader, Segmented, Skeleton } from "../../components/ui";

const REASON_LABELS = {
  forgot: "Esqueci de usar",
  "no-value": "Não vi tanto valor",
  "too-hard": "Achei difícil",
  "other-tool": "Uso outra ferramenta",
  "no-time": "Estou sem tempo",
  "too-expensive": "Está caro",
  "not-using": "Não estou usando",
  "missing-feature": "Falta funcionalidade",
  other: "Outro motivo",
};

const percent = (part, base) => (base ? `${Math.round((part / base) * 100)}%` : "—");

// Telas do onboarding na ordem em que aparecem
const ONBOARDING_STEPS = [
  ["basico", "Nome e celular"],
  ["dores", "Dores"],
  ["tentou", "O que já tentou"],
  ["esquece", "Contas e esquecimentos"],
  ["montando", "Montando o plano"],
  ["plano", "Plano pronto"],
  ["whatsapp", "Primeira mensagem"],
];

const FunnelStep = ({ label, value, base, detail }) => (
  <Card className="p-5">
    <p className="text-[15px] text-neutral-500 dark:text-neutral-400">{label}</p>
    <p className="mt-2 text-[28px] font-medium leading-none tracking-[-0.03em] text-neutral-900 dark:text-white">{value}</p>
    <p className="mt-2 text-xs text-neutral-500">{base !== undefined ? `${percent(value, base)} da etapa anterior` : detail}</p>
  </Card>
);

// Uma linha do funil: visitantes no passo, % do passo anterior e a meta de referência (artigos do X).
// Abaixo da meta fica em laranja.
const StepRow = ({ label, value, prev, goal }) => {
  const rate = prev ? Math.round((value / prev) * 100) : null;
  const below = goal && rate !== null && rate < goal;
  return (
    <li className="py-2.5">
      <div className="flex items-center gap-3 text-sm">
        <span className="flex-1 text-neutral-700 dark:text-neutral-300">{label}</span>
        <b className="w-14 text-right font-medium text-neutral-900 dark:text-neutral-100">{value}</b>
        <span className={`w-28 text-right text-xs ${below ? "text-amber-500" : "text-neutral-400"}`}>
          {rate === null ? "" : `${rate}%`}{goal ? ` · meta ${goal}%` : ""}
        </span>
      </div>
      {rate !== null && (
        <div className="mt-1.5 h-1 rounded-full bg-neutral-100 dark:bg-white/[0.05]">
          <div className={`h-full rounded-full ${below ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(rate, 100)}%` }} />
        </div>
      )}
    </li>
  );
};

const ReasonList = ({ title, items }) => (
  <Card className="p-5">
    <CardTitle>{title}</CardTitle>
    {items.length === 0 ? <p className="mt-4 text-sm text-neutral-400">Sem respostas ainda</p> : (
      <ul className="mt-3 divide-y divide-neutral-100 dark:divide-white/[0.05]">
        {items.map((i) => (
          <li key={i._id || "sem"} className="flex justify-between py-2 text-sm">
            <span className="text-neutral-700 dark:text-neutral-300">{REASON_LABELS[i._id] || i._id || "Sem motivo"}</span>
            <b className="font-medium">{i.total}</b>
          </li>
        ))}
      </ul>
    )}
  </Card>
);

const FEEDBACK_TABS = [["cancel", "Pedidos de cancelamento"], ["inactive", "Respostas de inatividade"]];

// Funil de ativação e respostas dos usuários (cancelamentos e motivos de inatividade)
export const Growth = () => {
  const [days, setDays] = useState(30);
  const [funnel, setFunnel] = useState(null);
  const [steps, setSteps] = useState(null);
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [tab, setTab] = useState("cancel");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [f, s, feedbackResponse] = await Promise.all([getFunnel(days), getFunnelSteps(days), getFeedback()]);
      setFunnel(f);
      setSteps(s);
      setFeedbackItems(feedbackResponse.items || []);
    } catch (err) {
      toast.error("Erro ao carregar o crescimento");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const markStatus = async (id, status) => {
    await setFeedbackStatus(id, status);
    setFeedbackItems((items) => items.map((i) => (i._id === id ? { ...i, status } : i)));
  };

  const visibleItems = feedbackItems.filter((f) => f.type === tab);
  const tabOptions = FEEDBACK_TABS.map(([value, label]) => ({
    value, label, count: feedbackItems.filter((f) => f.type === value && (value !== "cancel" || f.status === "open")).length,
  }));

  return (
    <Page>
      <PageHeader title="Crescimento" description="Funil das contas criadas no período e o que os usuários responderam.">
        <Segmented options={[7, 30, 90, 180].map((d) => ({ value: d, label: `${d} dias` }))} value={days} onChange={setDays} />
      </PageHeader>

      {loading && !funnel ? (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
          {Array.from({ length: 6 }, (_, i) => <Card key={i} className="p-5"><Skeleton className="h-4 w-24" /><Skeleton className="mt-3 h-8 w-14" /><Skeleton className="mt-3 h-3 w-28" /></Card>)}
        </div>
      ) : funnel && (
        <>
          <div className={`grid grid-cols-2 gap-4 transition-opacity md:grid-cols-3 xl:grid-cols-6 ${loading ? 'opacity-60' : ''}`}>
            <FunnelStep label="Contas criadas" value={funnel.accountsCreated} detail={`${funnel.bySource.site} site · ${funnel.bySource.gateway} gateway`} />
            <FunnelStep label="Onboarding concluído" value={funnel.onboardingCompleted} base={funnel.bySource.site + funnel.bySource.gateway} />
            <FunnelStep label="1º registro com a Nath" value={funnel.firstEntry} base={funnel.accountsCreated} />
            <FunnelStep label="Assinatura ativa" value={funnel.activeSubscription} base={funnel.accountsCreated} />
            <FunnelStep label="Ativos na semana 1" value={funnel.retentionWeek1.active} detail={`${percent(funnel.retentionWeek1.active, funnel.retentionWeek1.base)} de ${funnel.retentionWeek1.base} com 14+ dias`} />
            <FunnelStep label="Ativos na semana 4" value={funnel.retentionWeek4.active} detail={`${percent(funnel.retentionWeek4.active, funnel.retentionWeek4.base)} de ${funnel.retentionWeek4.base} com 35+ dias`} />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <ReasonList title="Por que pararam de usar" items={funnel.inactiveReasons} />
            <ReasonList title="Motivos de cancelamento" items={funnel.cancellations} />
          </div>
        </>
      )}

      {steps && (() => {
        const onboarding = ONBOARDING_STEPS.map(([id, label]) => [label, steps.app.onboarding.find((o) => o.step === id)?.total || 0]);
        const appRows = [
          ["Abriu o cadastro", steps.app.signupViews],
          ["Criou a conta", steps.app.signups],
          ...onboarding,
          ["Viu os planos", steps.app.plansViews, 75],
          ["Clicou em assinar", steps.app.plansClicks],
          ["Pagou", steps.app.paid, 10],
        ];
        return (
          <div className="grid gap-4 md:grid-cols-2">
            <Card className="p-5">
              <CardTitle>Funil do site (LP) · visitantes únicos</CardTitle>
              <ul className="mt-2 divide-y divide-neutral-100 dark:divide-white/[0.05]">
                <StepRow label="Visitou a LP" value={steps.lp.views} />
                <StepRow label="Clicou num plano (checkout)" value={steps.lp.checkoutClicks} prev={steps.lp.views} />
              </ul>
              <p className="mt-4 rounded-2xl bg-neutral-50 p-3 text-xs leading-relaxed text-neutral-500 dark:bg-white/[0.03]">
                Meta "Viu os planos" = 75% de quem começou o onboarding. Meta "Pagou" = 10% de quem viu os planos. Abaixo da meta fica em laranja: onboarding fraco ou oferta fraca.
              </p>
            </Card>
            <Card className="p-5">
              <CardTitle>Funil do app · cada etapa</CardTitle>
              <ul className="mt-2 divide-y divide-neutral-100 dark:divide-white/[0.05]">
                {appRows.map(([label, value, goal], i) => {
                  // A meta dos planos compara com o início do onboarding; as outras, com a etapa anterior
                  const prev = goal === 75 ? onboarding[0][1] : i > 0 ? appRows[i - 1][1] : undefined;
                  return <StepRow key={label} label={label} value={value} prev={prev} goal={goal} />;
                })}
              </ul>
            </Card>
          </div>
        );
      })()}

      <Card className="overflow-hidden">
        <div className="border-b border-neutral-100 p-4 dark:border-white/[0.05]">
          <Segmented options={tabOptions} value={tab} onChange={setTab} />
        </div>
        {visibleItems.length === 0 ? <EmptyState icon={MessageSquareQuote} title="Nada por aqui ainda" /> : (
          <ul className="divide-y divide-neutral-100 dark:divide-white/[0.05]">
            {visibleItems.map((f) => (
              <li key={f._id} className="flex flex-wrap items-start gap-4 p-4 sm:px-5">
                <Avatar name={f.user?.username} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-neutral-900 dark:text-neutral-100">
                    {f.user?.username || "—"} <span className="text-neutral-500">· {f.user?.email} · {f.user?.phone}</span>
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-neutral-500">
                    <span>{new Date(f.createdAt).toLocaleString("pt-BR")}</span>
                    {f.reason && <Badge>{REASON_LABELS[f.reason] || f.reason}</Badge>}
                    {f.rating ? <span className="flex items-center gap-0.5 text-amber-500">{Array.from({ length: f.rating }, (_, i) => <Star key={i} className="size-3 fill-current" />)}</span> : null}
                    {f.allowPublic && <Badge tone="green">Pode usar no site</Badge>}
                  </div>
                  {f.text && <p className="mt-2 rounded-2xl bg-neutral-50 px-3 py-2 text-sm text-neutral-700 dark:bg-white/[0.03] dark:text-neutral-300">“{f.text}”</p>}
                </div>
                {f.type === "cancel" && (
                  f.status === "open"
                    ? <Button icon={Check} className="h-9" onClick={() => markStatus(f._id, "done")}>Marcar como cancelado no gateway</Button>
                    : <Button variant="ghost" icon={RotateCcw} className="h-9" onClick={() => markStatus(f._id, "open")}>Processado · desfazer</Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </Page>
  );
};
